/* eslint-disable no-template-curly-in-string */
import { camelCase, isNil, template } from 'lodash';

import { isAdmin, isCcmModerator, isInfluencerRealm, isPayoutRealm } from './account';
import { getCampaignOfferUrl } from './campaign';
import { __t, applyTranslations } from './language';
import { insertIf } from 'utils/list';
import {
  ORGANIZATION_TYPE_INFLUENCERS_AGENCY,
  getBrandProfileId,
  getOrganizationProfileType,
  isBrandsFocusedOrganization,
  isCcmOnlyFocusedOrganization,
  isConnectedToBrandAgencyOrganization,
  isInfluencersFocusedOrganization,
  isInfluencersFocusedOrganizationRejected,
  isPlainBrandOrganization,
  organizationHasApprovedProfile
} from './organization';
import { hasProductListPage } from './products';
import { APPROVAL_STATUS_APPROVED, getApprovalStatus } from './profile';
import { isAccepted as isSubcampaignAccepted } from './subcampaign';

import type {
  Account,
  CampaignOverview,
  NavigationNavItem,
  Organization,
  Role,
  Subcampaign
} from 'types';
import type { SideNavigationItem } from 'components/side-navigation-v2/SideNavigation.type';

applyTranslations(require('components/navigation/navigation.i18n.json'));

type StringOrNumber = string | number;

const ADMIN = {
  glossaryKey: __t('navigation.admin'),
  trackingKey: 'admin',
  url: '/admin',
  includes: ['/admin/.*']
};

const MISC: NavigationNavItem = {
  glossaryKey: 'Misc',
  trackingKey: 'miscellaneous',
  url: '/miscellaneous',
  includes: ['/miscellaneous/.*'],
  isFreemiumAllowed: true
};

const DISCOVERY = {
  glossaryKey: __t('navigation.influencers_discovery'),
  trackingKey: 'search',
  url: '/discovery/creators',
  requiresApproval: true,
  isFreemiumAllowed: true,
  includes: [
    '/discovery/creators/[0-9]+.*',
    '/discovery/creators\\?.+',
    '/discovery/content',
    '/content-creator/profiles/[0-9]+.*'
  ]
};

const DISCOVERY_CREATORS = {
  key: 'discovery-creators',
  glossaryKey: __t('navigation.influencers'),
  trackingKey: 'search',
  url: '/discovery/creators',
  requiresApproval: true,
  isFreemiumAllowed: true,
  includes: [
    '/discovery/creators/[0-9]+.*',
    '/discovery/creators\\?.+'
  ]
};

const DISCOVERY_CONTENT = {
  key: 'discovery-content',
  glossaryKey: __t('navigation.content_discovery'),
  trackingKey: 'content-discovery',
  url: '/discovery/content',
  requiresApproval: true,
  includes: [
    '/discovery/content'
  ]
};

const COLLECTIONS = {
  key: 'collection',
  glossaryKey: __t('navigation.collections'),
  trackingKey: 'collections',
  url: '/collections/creators',
  requiresApproval: true,
  includes: ['/collections/creators.*', '/collections/content.*']
};

const COLLECTIONS_CREATORS = {
  glossaryKey: __t('navigation.creator_collections'),
  trackingKey: 'collections-creators',
  url: '/collections/creators',
  requiresApproval: true,
  includes: ['/collections/creators.*']
};

const COLLECTIONS_CONTENT = {
  glossaryKey: __t('navigation.content_collections'),
  trackingKey: 'collections-content',
  url: '/collections/content?tab=smart',
  requiresApproval: true,
  includes: ['/collections/content.*']
};

const INSIGHTS = {
  key: 'insights',
  glossaryKey: __t('navigation.insights'),
  trackingKey: 'insights',
  url: '/insights',
  requiresApproval: true,
  includes: ['/insights.*']
};

const INFLUENCERS_AGENCY_SETTINGS = {
  key: 'influencers_agency',
  glossaryKey: __t('navigation.talent_agency_settings'),
  trackingKey: 'influencers-agency-settings',
  url: '/influencers-agencies/${agencyId}/settings/profiles-overview',
  requiresApproval: false,
  includes: ['/influencers-agencies/[0-9]+.*']
};

const INSIGHTS_SOV = {
  glossaryKey: __t('navigation.share_of_voice'),
  trackingKey: 'sov',
  url: '/insights/sov',
  requiresApproval: true,
  includes: ['/insights/sov.*']
};

const INSIGHTS_REPORTS = {
  glossaryKey: __t('navigation.insights_reports'),
  trackingKey: 'insights-reports',
  url: '/insights/reports',
  requiresApproval: true,
  isBeta: true,
  includes: ['/insights/reports.*']
};

const HELP = {
  glossaryKey: __t('navigation.help'),
  trackingKey: 'help',
  external: true
};

const CAMPAIGNS = {
  key: 'campaign',
  glossaryKey: __t('navigation.campaigns'),
  trackingKey: 'campaigns',
  url: '/organizations/${id}/campaigns',
  requiresApproval: true,
  notificationType: 'campaigns',
  includes: [
    '/organizations/[0-9]+/campaigns.*',
    '/campaigns/[0-9]+.*',
    '/campaigns/ccm/[0-9]+.*'
  ]
};

const CAMPAIGN_REPORT_REBUILD = {
  glossaryKey: __t('navigation.campaign_subnavigation_reports'),
  trackingKey: 'campaign-report-beta',
  url: '/campaigns/${id}/report/v2',
  includes: [
    '/campaigns/[0-9]+.*/report/v2'
  ]
};

const CAMPAIGN_PAYMENTS = {
  glossaryKey: __t('navigation.campaign_subnavigation_payments'),
  trackingKey: 'campaign-payments',
  url: '/campaigns/${id}/payments'
};

const CAMPAIGN_PRODUCT_LIST = {
  glossaryKey: __t('navigation.campaign_subnavigation_product_lists'),
  trackingKey: 'campaign-product-lists',
  url: '/campaigns/${id}/product-lists'
};

const CAMPAIGN_DELIVERABLES = {
  glossaryKey: __t('navigation.campaign_subnavigation_deliverables'),
  trackingKey: 'campaign-deliverables',
  url: '/campaigns/${id}/deliverables'
};

function getCampaignOffersUrl(overview?: CampaignOverview | null): NavigationNavItem {
  return {
    glossaryKey: __t('navigation.campaign_subnavigation_offers'),
    trackingKey: 'campaign-offers',
    url: getCampaignOfferUrl('${id}', overview),
    includes: [
      '/campaigns/[0-9]+/subcampaigns/.*',
      '/campaigns/[0-9]+/participants/.*'
    ]
  };
}

const CAMPAIGN_MESSAGES = {
  glossaryKey: __t('navigation.campaign_subnavigation_messages'),
  trackingKey: 'campaign-messages',
  url: '/campaigns/${id}/messages'
};

const SUBCAMPAIGN_OFFER = {
  glossaryKey: __t('navigation.campaign_subnavigation_brief'),
  trackingKey: 'subcampaign-offer',
  url: '/campaigns/${id}/details/${subcampaignId}'
};

const SUBCAMPAIGN_MESSAGES = {
  glossaryKey: __t('navigation.subcampaign_subnavigation_messages'),
  trackingKey: 'subcampaign-messages',
  url: '/campaigns/${id}/details/${subcampaignId}/messages'
};

const SUBCAMPAIGN_PAYMENTS = {
  glossaryKey: __t('navigation.campaign_subnavigation_payments'),
  trackingKey: 'subcampaign-payments',
  url: '/campaigns/${id}/details/${subcampaignId}/payments'
};

const CCM_CAMPAIGN_OVERVIEW = {
  glossaryKey: __t('navigation.ccm_campaign_subnavigation_overview'),
  trackingKey: 'ccm-campaign-overview',
  url: '/campaigns/ccm/${id}',
  includes: [
    '/campaigns/ccm/[0-9]+/overview',
    '/campaigns/ccm/[0-9]+/participants/.*',
    '/campaigns/ccm/[0-9]+/subcampaigns/.*'
  ]
};

const CCM_CAMPAIGN_MESSAGES = {
  glossaryKey: __t('navigation.campaign_subnavigation_messages'),
  trackingKey: 'ccm-campaign-messages',
  url: '/campaigns/ccm/${id}/messages'
};

const CCM_CAMPAIGN_OUTFITS = {
  glossaryKey: __t('navigation.ccm_campaign_subnavigation_outfits'),
  trackingKey: 'ccm-campaign-outfits',
  url: '/campaigns/ccm/${id}/outfits'
};

const CCM_CAMPAIGN_CONTENT = {
  glossaryKey: __t('navigation.ccm_campaign_subnavigation_content'),
  trackingKey: 'ccm-campaign-content',
  url: '/campaigns/ccm/${id}/deliverables'
};

const CCM_CAMPAIGN_REPORTING = {
  glossaryKey: __t('navigation.ccm_campaign_subnavigation_reporting'),
  trackingKey: 'ccm-campaign-reporting',
  url: '/campaigns/ccm/${id}/report'
};

const CCM_CAMPAIGN_PAYMENTS = {
  glossaryKey: __t('navigation.ccm_campaign_subnavigation_payments'),
  trackingKey: 'ccm-campaign-payments',
  url: '/campaigns/ccm/${id}/payments'
};

const SETTINGS_URL_COMMON = '/organizations/${id}/settings/${subId}';
const BRAND_CREATE_PROFILE_URL = '/organizations/${id}/brands/create';
const BRAND_SETTINGS_URL_COMMON = '/organizations/${id}/brand/settings';
const BRAND_SETTINGS_EDIT_URL_COMMON = '/organizations/${id}/brand/settings/edit';
const AGENCY_SETTINGS_URL_COMMON = '/agency/${agencyOrganizationId}/settings/${subId}';
const AGENCY_OVERVIEW_URL_COMMON = '/agency/${agencyOrganizationId}/overview';
const INFLUENCERS_AGENCY_SETTINGS_URL_COMMON = '/influencers-agencies/${agencyId}/settings/${subId}';

const SETTINGS = {
  brandProfile: {
    glossaryKey: __t('navigation.settings_navigation.brand_profile'),
    trackingKey: 'brand_profile',
    url: BRAND_SETTINGS_URL_COMMON,
    includes: [BRAND_SETTINGS_EDIT_URL_COMMON]
  },
  createBrandProfile: {
    glossaryKey: __t('navigation.settings_navigation.create_brand_profile'),
    trackingKey: 'create_brand_profile',
    url: BRAND_CREATE_PROFILE_URL
  },
  account: {
    glossaryKey: __t('navigation.settings_navigation.account'),
    trackingKey: 'settings-account',
    url: '/user/settings',
    subId: 'account'
  },
  detailsBrand: {
    glossaryKey: __t('navigation.settings_navigation.details_brand'),
    trackingKey: 'settings-details',
    url: SETTINGS_URL_COMMON,
    subId: 'details'
  },
  detailsInfluencer: {
    glossaryKey: __t('navigation.settings_navigation.details_influencer'),
    trackingKey: 'settings-details',
    url: SETTINGS_URL_COMMON,
    subId: 'details'
  },
  subscriptions: {
    glossaryKey: __t('navigation.settings_navigation.subscriptions'),
    trackingKey: 'settings-subscriptions',
    url: SETTINGS_URL_COMMON,
    subId: 'subscriptions'
  },
  teamMembers: {
    glossaryKey: __t('navigation.settings_navigation.team_members'),
    trackingKey: 'settings-team-members',
    url: SETTINGS_URL_COMMON,
    subId: 'team-members'
  },
  emailNotifications: {
    glossaryKey: __t('navigation.settings_navigation.email_notifications'),
    trackingKey: 'settings-email-notifications',
    url: '/user/settings/email-notifications'
  },
  agencyProfile: {
    glossaryKey: __t('navigation.settings_navigation.agency_profile'),
    trackingKey: 'agency_profile',
    url: AGENCY_SETTINGS_URL_COMMON,
    subId: 'profile',
    includes: ['/agency/[0-9]+/settings/profile/edit', '/agency/[0-9]+/settings/profile/create']
  },
  editAgencyProfile: {
    glossaryKey: __t('navigation.settings_navigation.agency_profile_edit'),
    trackingKey: 'agency_profile_edit',
    url: AGENCY_SETTINGS_URL_COMMON,
    subId: 'profile/edit'
  },
  agencyDetails: {
    glossaryKey: __t('navigation.settings_navigation.agency_details'),
    trackingKey: 'agency_details',
    url: AGENCY_SETTINGS_URL_COMMON,
    subId: 'details'
  },
  agencySubscription: {
    glossaryKey: __t('navigation.settings_navigation.agency_subscriptions'),
    trackingKey: 'agency_subscriptions',
    url: AGENCY_SETTINGS_URL_COMMON,
    subId: 'subscriptions'
  },
  agencyOverview: {
    glossaryKey: __t('navigation.settings_navigation.agency_view'),
    trackingKey: 'agency_view',
    url: AGENCY_OVERVIEW_URL_COMMON,
    requiresApproval: true
  },
  profilesOverview: {
    glossaryKey: __t('navigation.settings_navigation.influencers_agency_profiles_overview'),
    trackingKey: 'influencers_agency_profiles_overview',
    url: INFLUENCERS_AGENCY_SETTINGS_URL_COMMON,
    subId: 'profiles-overview'
  },
  influencersAgencyProfile: {
    glossaryKey: __t('navigation.settings_navigation.influencers_agency_profile'),
    trackingKey: 'agency_details',
    url: INFLUENCERS_AGENCY_SETTINGS_URL_COMMON,
    subId: 'profile',
    includes: ['/influencers-agencies/[0-9]+/settings/profile/edit', '/influencers-agencies/[0-9]+/settings/profile/create']
  },
  influencersAgencyBusinessDetails: {
    glossaryKey: __t('navigation.settings_navigation.influencers_agency_business_details'),
    trackingKey: 'influencers_agency_business_details',
    url: INFLUENCERS_AGENCY_SETTINGS_URL_COMMON,
    subId: 'business-details'
  },
  influencersAgencyTeamMembers: {
    glossaryKey: __t('navigation.settings_navigation.influencers_agency_team_members'),
    trackingKey: 'influencers_agency_team_members',
    url: INFLUENCERS_AGENCY_SETTINGS_URL_COMMON,
    subId: 'team-members'
  }
};

const ORGANIZATION_NAVIGATION_BY_TYPE = {
  influencer: [CAMPAIGNS],
  brand: [INSIGHTS, DISCOVERY, CAMPAIGNS, COLLECTIONS],
  influencers_agency: [INFLUENCERS_AGENCY_SETTINGS],
  ccm: [CAMPAIGNS]
};

const SETTINGS_BY_TYPE = {
  fallback: [SETTINGS.account],
  brandAccountSettings: [SETTINGS.account, SETTINGS.emailNotifications],
  influencerAccountSettings: [SETTINGS.account],
  influencersAgencyAccountSettings: [SETTINGS.account],
  agencySettings: [SETTINGS.agencyOverview, SETTINGS.agencyProfile, SETTINGS.agencyDetails, SETTINGS.agencySubscription],
  influencersAgencySettings: [SETTINGS.profilesOverview, SETTINGS.influencersAgencyProfile, SETTINGS.influencersAgencyBusinessDetails, SETTINGS.influencersAgencyTeamMembers]
};

const FALLBACK_URL = '/user/settings';

const ORGANIZATION_SETTINGS_BY_TYPE = {
  influencerSettings: [SETTINGS.detailsInfluencer, SETTINGS.teamMembers],
  emptyBrandSettings: [SETTINGS.createBrandProfile, SETTINGS.teamMembers],
  brandSettings: [SETTINGS.brandProfile, SETTINGS.detailsBrand, SETTINGS.subscriptions, SETTINGS.teamMembers],
  brandUnderAgencySettings: [SETTINGS.brandProfile, SETTINGS.teamMembers]
};

function getDefaultOrganizationSettingsByType(key, profileId) {
  if (key === 'influencerSettings') {
    return ORGANIZATION_SETTINGS_BY_TYPE.influencerSettings;
  }

  if (!profileId) {
    return ORGANIZATION_SETTINGS_BY_TYPE.emptyBrandSettings;
  }

  return ORGANIZATION_SETTINGS_BY_TYPE[key];
}

export function getDefaultInfluencerProfileUrl(organization: Organization): string {
  const influencerProfiles = organization.influencerProfiles || [];

  return influencerProfiles.length > 0 ?
    `/content-creator/profiles/${influencerProfiles[0].id}` :
    `/organizations/${organization.id}/content-creators/create`;
}

export function getDefaultOrganizationSettingsUrl(organization: Organization): string {
  if (!organization) {
    return FALLBACK_URL;
  }

  if (organization.type === ORGANIZATION_TYPE_INFLUENCERS_AGENCY && organization.influencersAgencyId) {
    return `/influencers-agencies/${organization.influencersAgencyId}/settings/business-details`;
  }

  if (isInfluencersFocusedOrganization(organization) && organization.influencerProfiles) {
    return `/organizations/${organization.id}/settings/details`;
  }

  if (isBrandsFocusedOrganization(organization) && organization.brandProfiles) {
    const brandProfileId = organization.brandProfiles.length
      ? organization.brandProfiles[0].id
      : null;

    return brandProfileId ?
      `/organizations/${organization.id}/brand/settings` :
      `/organizations/${organization.id}/brands/create`;
  }

  return FALLBACK_URL;
}

export function getDefaultOrganizationUrl(currentOrganization?: Organization | null): string {
  if (!currentOrganization) {
    // Right now it is possible for a user not to be a part of any organization
    return FALLBACK_URL;
  }

  if (currentOrganization.isFreemium) {
    return '/discovery/creators';
  }

  if (currentOrganization.type === ORGANIZATION_TYPE_INFLUENCERS_AGENCY && currentOrganization.influencersAgencyId) {
    return `/influencers-agencies/${currentOrganization.influencersAgencyId}/settings/profiles-overview`;
  }

  if (isInfluencersFocusedOrganization(currentOrganization)) {
    return currentOrganization.influencerProfiles && currentOrganization.influencerProfiles.length > 0 ?
      `/organizations/${currentOrganization.id}/campaigns` :
      `/organizations/${currentOrganization.id}/content-creators/create`;
  }

  if (!isPlainBrandOrganization(currentOrganization)) {
    return FALLBACK_URL;
  }

  const brandProfiles = currentOrganization.brandProfiles ?? [];
  const hasApprovedBrandProfile = brandProfiles && brandProfiles.length > 0 && getApprovalStatus(brandProfiles[0]) === APPROVAL_STATUS_APPROVED;

  if (hasApprovedBrandProfile) {
    return `/organizations/${currentOrganization.id}/campaigns`;
  }

  if (brandProfiles) {
    const brandProfileId = brandProfiles.length ? brandProfiles[0].id : null;

    return brandProfileId ?
      `/organizations/${currentOrganization.id}/brand/settings` :
      `/organizations/${currentOrganization.id}/brands/create`;
  }

  return FALLBACK_URL;
}

export function getDefaultProfileUrl(organization: Organization): string {
  if (isInfluencersFocusedOrganization(organization) && organization.influencerProfiles) {
    return getDefaultInfluencerProfileUrl(organization);
  }

  return getDefaultOrganizationUrl(organization);
}

export function getAgencyIdForOrganization(
  organization?: { agencyOrganizationId?: number } | null
) {
  return organization && organization.agencyOrganizationId;
}

export function getAdditionalNavigationForOrganization(organization?: Organization | null): Array<NavigationNavItem> {
  if (!organization) {
    return [];
  }

  const helpLink = isBrandsFocusedOrganization(organization) ?
    'https://help.collabary.com/brand-faqs' :
    'https://help.collabary.com/content-creator-faqs';

  return [
    {
      ...HELP,
      url: helpLink
    }
  ];
}

export function getNavigationForOrganization(
  organization: Organization,
  currentAccount: Account,
  brandsIdsWithAccessToMisc: Array<number>,
  roles: Array<Role>
): Array<NavigationNavItem> {
  let adminCopy = { ...ADMIN } as NavigationNavItem;
  let navigation: Array<NavigationNavItem> = [];

  if (!organization) {
    return [];
  }

  // COLLAB-16613: we invite payout_realm role users to a freemium brand to limit access
  // adding `isFreemiumAllowed` forces the "Admin" nav link to appear for freemium brands
  if (isPayoutRealm(roles)) {
    adminCopy = {
      ...adminCopy,
      isFreemiumAllowed: true
    };
  }

  if (
    isAdmin(currentAccount, roles) || isInfluencerRealm(roles) || isPayoutRealm(roles)
    || isCcmModerator(roles)
  ) {
    navigation.push(adminCopy);
  }

  const organizationProfileType = getOrganizationProfileType(organization);

  navigation = isCcmOnlyFocusedOrganization(organization)
    ? navigation.concat(ORGANIZATION_NAVIGATION_BY_TYPE.ccm)
    : navigation.concat(ORGANIZATION_NAVIGATION_BY_TYPE[organizationProfileType]);

  const hasApprovedProfile = organizationHasApprovedProfile(organization) || organization.isFreemium; // COLLAB-12525
  const isInfuencerProfileRejected = isInfluencersFocusedOrganizationRejected(organization);

  const visiblityFilter = (nav) => (
    !nav.requiresApproval ||
    nav.requiresApproval === hasApprovedProfile ||
    nav.requiresApproval === isInfuencerProfileRejected
  );
  const visibleNavigation = navigation.filter(visiblityFilter);

  let influencerProfileId: number | string | null = null;
  if (isInfluencersFocusedOrganization(organization) && organization.influencerProfiles) {
    influencerProfileId = organization.influencerProfiles.length ? organization.influencerProfiles[0].id : 'create';
  }

  let brandProfileId: number | string | null = null;
  if (isBrandsFocusedOrganization(organization) && organization.brandProfiles) {
    brandProfileId = organization.brandProfiles.length ? organization.brandProfiles[0].id : 'create';
  }

  let agencyId: number | string | null = null;
  if (organization.type === ORGANIZATION_TYPE_INFLUENCERS_AGENCY) {
    agencyId = organization.influencersAgencyId || null;
  }

  const mainItems = visibleNavigation.map((item) => ({
    ...item,
    url: template(item.url)({
      id: organization.id,
      influencerProfileId,
      brandProfileId,
      agencyId
    }),
    includes: (item.includes || []).map(includedUrl => template(includedUrl)({
      id: organization ? organization.id : null
    }))
  }));

  if (
    brandProfileId === null
    || brandProfileId === 'create'
    || (typeof brandProfileId === 'number' && !brandsIdsWithAccessToMisc.includes(brandProfileId))
    || organization.isFreemium // COLLAB-12525
    || organization.ccmStatus === 'only'
  ) {
    return mainItems;
  }

  return [
    ...mainItems,
    MISC
  ];
}

export const getDiscoverySubnavigation = (orgId: number): Array<NavigationNavItem> => {
  if (!orgId) {
    return [];
  }

  const items = [
    DISCOVERY_CREATORS,
    DISCOVERY_CONTENT
  ];

  return items.map(item => ({
    ...item,
    url: template(item.url)({ id: orgId })
  }));
};

export const getCollectionsSubnavigation = (orgId: number): Array<NavigationNavItem> => {
  if (!orgId) {
    return [];
  }

  const items = [
    COLLECTIONS_CREATORS,
    COLLECTIONS_CONTENT
  ];

  return items.map(item => ({
    ...item,
    url: template(item.url)({ id: orgId })
  }));
};


export const getInsightsSubnavigation = (
  orgId: number,
  showReportsLink: boolean
): Array<NavigationNavItem> => {
  if (!orgId) {
    return [];
  }

  const items = [
    INSIGHTS_SOV,
    ...insertIf(showReportsLink, INSIGHTS_REPORTS)
  ];

  return items.map(item => ({
    ...item,
    url: template(item.url)({ id: orgId })
  }));
};

export function getCampaignSubnavigation(
  campaignId?: StringOrNumber | null,
  overview?: CampaignOverview | null,
  organization?: Organization | null,
  campaignProductListAccessIds?: string
): Array<NavigationNavItem> {
  if (!campaignId) {
    return [];
  }

  const campaignProductListPageEnabled = hasProductListPage(
    organization,
    campaignProductListAccessIds
  );

  const items = [
    getCampaignOffersUrl(overview),
    CAMPAIGN_MESSAGES,
    ...insertIf(campaignProductListPageEnabled, CAMPAIGN_PRODUCT_LIST),
    CAMPAIGN_DELIVERABLES,
    { ...CAMPAIGN_REPORT_REBUILD },
    CAMPAIGN_PAYMENTS
  ];

  return items.map((item) => ({
    ...item,
    url: template(item.url)({ id: campaignId })
  }));
}

export function getCcmCreatorlessCampaignSubnavigation(
  campaignId: StringOrNumber
): Array<NavigationNavItem> {
  const updatedOverviewRoute = {
    ...CCM_CAMPAIGN_OVERVIEW,
    url: `${CCM_CAMPAIGN_OVERVIEW.url}/overview`
  };
  const items = [
    updatedOverviewRoute,
    CCM_CAMPAIGN_REPORTING
  ];

  return items.map((item) => ({
    ...item,
    url: template(item.url)({
      id: campaignId
    })
  }));
}

export function getCcmCampaignSubnavigation(
  campaignId: StringOrNumber | null,
  isModerator: boolean
): Array<NavigationNavItem> {
  if (!campaignId) {
    return [];
  }

  let updatedOverviewRoute;

  if (isModerator) {
    updatedOverviewRoute = {
      ...CCM_CAMPAIGN_OVERVIEW,
      glossaryKey: __t('navigation.ccm_campaign_subnavigation_offers'),
      url: `${CCM_CAMPAIGN_OVERVIEW.url}/participants/accepted`
    };
  } else {
    updatedOverviewRoute = {
      ...CCM_CAMPAIGN_OVERVIEW,
      url: `${CCM_CAMPAIGN_OVERVIEW.url}/overview`
    };
  }

  const items = [
    updatedOverviewRoute,
    ...insertIf(isModerator, CCM_CAMPAIGN_MESSAGES),
    ...insertIf(!isModerator, CCM_CAMPAIGN_OUTFITS),
    ...insertIf(isModerator, CCM_CAMPAIGN_CONTENT),
    CCM_CAMPAIGN_REPORTING,
    CCM_CAMPAIGN_PAYMENTS
  ];

  return items.map((item) => ({
    ...item,
    url: template(item.url)({
      id: campaignId
    })
  }));
}

export function getSubcampaignSubnavigation(
  campaignId: StringOrNumber | undefined | null,
  subcampaignId: StringOrNumber | undefined | null,
  subcampaign: Subcampaign,
  organization?: Organization | null
): Array<NavigationNavItem> {
  if (!campaignId || !subcampaignId) {
    return [];
  }

  const items = [SUBCAMPAIGN_OFFER];
  const isInfuencerProfileRejected = isInfluencersFocusedOrganizationRejected(organization);

  if (!isInfuencerProfileRejected) {
    items.push(SUBCAMPAIGN_MESSAGES);
  }

  if (subcampaign && isSubcampaignAccepted(subcampaign)) {
    items.push(SUBCAMPAIGN_PAYMENTS);
  }

  return items.map(item => ({
    ...item,
    url: template(item.url)({
      id: campaignId,
      subcampaignId
    })
  }));
}

export function getInfluencersAgencySettingsSubnavigation(
  agencyId: number,
  isSpecialtyAdminPage?: boolean | null,
  isAccountPartOfOrg?: boolean): Array<NavigationNavItem> {
  return SETTINGS_BY_TYPE.influencersAgencySettings.map(item => {
    let url = template(item.url)({
      agencyId,
      subId: ('subId' in item)
        ? item.subId ?? null
        : null
    });

    if (isSpecialtyAdminPage) {
      url = `/admin${url}`;
    }

    return ({
      ...item,
      url
    });
  }).filter(item => isAccountPartOfOrg || item.subId !== 'business-details');
}

export function getAgencySettingsSubnavigation(agencyId: number, isSpecialtyAdminPage?: boolean | null): Array<NavigationNavItem> {
  return SETTINGS_BY_TYPE.agencySettings.map(item => {
    let url = template(item.url)({
      agencyOrganizationId: agencyId,
      subId: ('subId' in item)
        ? item.subId ?? null
        : null,
      orgType: 'brand'
    });

    if (isSpecialtyAdminPage) {
      url = `/admin${url}`;
    }

    return ({
      ...item,
      url
    });
  });
}

export function getAgencyWithoutSettingsSubnavigation(id: number) {
  const url = `/agency/${id}/create`;

  return [
    {
      ...SETTINGS.agencyProfile,
      url
    }
  ];
}

export function getUserSettingsSubnavigation(organization?: Organization | null): Array<NavigationNavItem> {
  if (!organization) {
    return SETTINGS_BY_TYPE.fallback;
  }

  const organizationProfileType = getOrganizationProfileType(organization);
  const key = `${camelCase(organizationProfileType)}AccountSettings`;

  return SETTINGS_BY_TYPE[key];
}

export function getSettingsNavigation(organization?: Organization | null): Array<NavigationNavItem> {
  const organizationProfileType = getOrganizationProfileType(organization);

  if (!organization || !organizationProfileType) {
    return [];
  }

  let key = `${organizationProfileType}Settings`;

  if (isPlainBrandOrganization(organization) && isConnectedToBrandAgencyOrganization(organization)) {
    key = 'brandUnderAgencySettings';
  }

  const brandId = getBrandProfileId(organization);
  const navigation = [...getDefaultOrganizationSettingsByType(key, brandId)];

  return navigation.map(item => ({
    ...item,
    url: template(item.url)({
      id: organization ? organization.id : null,
      brandId,
      subId: item.subId || null
    }),
    includes: (item.includes || []).map(includedUrl => template(includedUrl)({
      id: organization ? organization.id : null
    }))
  }));
}

export function isActive(item: NavigationNavItem, pathname: string): boolean {
  if (isNil(pathname)) {
    return false;
  }

  // if a `#` is included in the path, then path separation gets borked.
  // rather than focus on `#`, just split by `?` if it's in a borked state.
  const updatedPathname = pathname.includes('?')
    ? pathname.split('?')[0]
    : pathname;

  if (item.url === updatedPathname) {
    return true;
  }

  return (item.includes || []).some((url) => (new RegExp(url)).test(pathname));
}

export function mapNavigationNavItemToSideNavigationItem(item: NavigationNavItem): SideNavigationItem {
  const { url, includes, trackingKey, glossaryKey } = item;

  return {
    type: 'item',
    url,
    includes,
    trackingKey,
    title: glossaryKey,
    isAvailable: true
  };
}
